import React, {useEffect, useRef, useState} from 'react'
import {gsap} from 'gsap'
import Pearl_1 from './_assets/pearl_1.webp'
import Pearl_2 from './_assets/pearl_2.webp'
import Pearl_3 from './_assets/pearl_3.webp'

import Styles from './Pearl.module.css'

interface PearlProps {
  pearlTrigger: boolean
  backgroundSize?: string
  setTl?: React.Dispatch<React.SetStateAction<gsap.core.Timeline | null>>
}

const Pearl: React.FC<PearlProps> = ({pearlTrigger, backgroundSize = '100% 100%', setTl}) => {
  const pearlRef1 = useRef<HTMLDivElement>(null)
  const pearlRef2 = useRef<HTMLDivElement>(null)
  const pearlRef3 = useRef<HTMLDivElement>(null)
  const [time, setTime] = useState(Math.random() * 12)
  const trigger = useRef(false)

  const createTimeline = (): gsap.core.Timeline | null => {
    const pearl1 = pearlRef1.current
    const pearl2 = pearlRef2.current
    const pearl3 = pearlRef3.current
    const d = 0.5

    if (pearl1 && pearl2 && pearl3) {
      const _tl = gsap.timeline({paused: true, repeat: -1, defaults: {ease: 'linear', duration: d}})
      const pos1 = {x: 0, y: 0}
      const pos2 = {x: 0, y: 0}
      const pos3 = {x: 0, y: 0}

      const setRandom = (pos: {x: number; y: number}) => {
        pos.x = Math.random() * 50
        pos.y = Math.random() * 100
      }

      const setPosition = (pos: {x: number; y: number}, pearl: gsap.TweenTarget, offset: number, progress: number) => {
        const x = 100 - (pos.x + (progress * 0.5 + offset) * 50)
        gsap.set(pearl, {
          backgroundPosition: `${x}% ${pos.y}%`,
        })
      }

      setRandom(pos1)
      setRandom(pos2)
      setRandom(pos3)

      gsap.set(pearl1, {opacity: 1})

      _tl
        // fadein pearl2
        .to(
          pearl2,
          {
            opacity: 1,
            onStart() {
              setRandom(pos2)
            },
            onUpdate() {
              const progress = this.progress()
              setPosition(pos2, pearl2, 0, progress)
              gsap.set(pearl2, {opacity: progress})
            },
          },
          0,
        )

        // fadein pearl3
        .to(
          pearl3,
          {
            onStart() {
              setRandom(pos3)
            },
            onUpdate() {
              const progress = this.progress()
              setPosition(pos2, pearl2, 0.5, progress)
              setPosition(pos3, pearl3, 0, progress)
              gsap.set(pearl3, {opacity: progress})
              gsap.set(pearl2, {opacity: 1 - progress})
            },
          },
          d,
        )

        // fadeout pearl3 then appear pearl1
        .to(
          pearl3,
          {
            onStart() {
              setRandom(pos1)
            },
            onUpdate() {
              const progress = this.progress()
              setPosition(pos3, pearl3, 0.5, progress)
              gsap.set(pearl3, {opacity: 1 - progress})
            },
          },
          d * 2,
        )

      _tl.time(time)

      return _tl
    }

    return null
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const tl = createTimeline()

    if (setTl) {
      setTl(tl)
    }

    if (!mediaQuery.matches) {
      if (tl) {
        let lastTime = performance.now()

        const ticker = () => {
          const now = performance.now()
          const delta = (now - lastTime) / 1000 // delta in seconds
          lastTime = now

          if (trigger.current) {
            setTime(prev => {
              const newTime = prev + delta
              tl.time(newTime)
              return newTime
            })
          }
        }
        gsap.ticker.add(ticker)

        return () => {
          gsap.ticker.remove(ticker)
          tl.kill()
        }
      }
    } else {
      if (tl) tl.time(time)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    trigger.current = pearlTrigger
  }, [pearlTrigger])

  const pearlStyle = {
    backgroundSize,
  }

  return (
    <>
      <div
        className={Styles.Pearl}
        ref={pearlRef1}
        style={{
          ...pearlStyle,
          backgroundImage: `url(${Pearl_1})`,
        }}
      />
      <div
        className={Styles.Pearl}
        ref={pearlRef2}
        style={{
          ...pearlStyle,
          backgroundImage: `url(${Pearl_2})`,
        }}
      />
      <div
        className={Styles.Pearl}
        ref={pearlRef3}
        style={{
          ...pearlStyle,
          backgroundImage: `url(${Pearl_3})`,
        }}
      />
    </>
  )
}

export default Pearl
